import type { DealBookingType } from '@src/types/OTC';
import { fixingIndexMappingMapToRecord, useFixingIndexMapping } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { useEffect, useReducer, useRef, type ReactNode } from 'react';
import {
  clearError,
  clearForm,
  dealProductBuilderSliceReducer,
  getInitialRowData,
  resetForm,
  setBookingType,
  setFixingIndexMapping,
  updateGridRowData,
  updateOTCFields,
} from '../dealProductBuilderSlice';
import { DealProductBuilderContext, type DealProductBuilderContextValue } from './useDealProductBuilder';

interface DealProductBuilderProviderProps {
  children: ReactNode;
  initialBookingType: DealBookingType;
}

export const DealProductBuilderProvider = ({ children, initialBookingType }: DealProductBuilderProviderProps) => {
  const fixingIndexMapping = useFixingIndexMapping();
  const [state, dispatch] = useReducer(dealProductBuilderSliceReducer, {
    rowData: getInitialRowData({
      product: initialBookingType,
    }),
    isFormValid: false,
    error: null,
    validationErrors: null,
    fixingIndexMapping: undefined,
  });

  // Get booking type from the first row if it exists, otherwise use initial booking type
  const firstRow = state.rowData.at(0);
  const bookingType = firstRow?.product || initialBookingType;

  const prevInitialBookingType = useRef(initialBookingType);

  // Update fixing index mapping in Redux state when it changes
  useEffect(() => {
    dispatch(
      setFixingIndexMapping(fixingIndexMapping.size > 0 ? fixingIndexMappingMapToRecord(fixingIndexMapping) : undefined)
    );
  }, [fixingIndexMapping]);

  // this is to help ensure the tab keeps the latest booking type based on
  // a) if it was navigated to OR
  // b) if the user changed the booking type
  useEffect(() => {
    if (prevInitialBookingType.current !== initialBookingType) {
      dispatch(setBookingType(initialBookingType));
      prevInitialBookingType.current = initialBookingType;
    }
  }, [initialBookingType]);

  const handleSetBookingType = (newBookingType: DealBookingType) => {
    dispatch(setBookingType(newBookingType));
  };

  const contextValue: DealProductBuilderContextValue = {
    bookingType,
    setBookingType: handleSetBookingType,
    state,
    dispatch,
    actions: {
      clearError,
      clearForm,
      resetForm,
      setBookingType,
      updateOTCFields,
      updateGridRowData,
      setFixingIndexMapping,
    },
  };

  return <DealProductBuilderContext.Provider value={contextValue}>{children}</DealProductBuilderContext.Provider>;
};
